import { useEffect, useState } from 'react';
import { CLOSED_STAGES, isClosed, STAGES, stageLabel, type Stage } from '@ouradent/shared';
import { useBoard, useMoveStage, useTeam } from '../lib/data';
import { useLocalState } from '../lib/useLocalState';
import type { BoardLead } from '../lib/types';
import { Icon } from '../ui/icons';
import { Chead, ErrorBox, SrcBadge, StepPill, useDrawer, useToast } from '../ui/kit';

export function useIsMobile() {
  const query = '(max-width: 760px)';
  const [mobile, setMobile] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

const OPEN = STAGES.filter((s) => !isClosed(s.id));
const CLOSED = STAGES.filter((s) => isClosed(s.id));

/** Lost, disqualified and nurture need a reason (move_stage enforces it). */
/** The line under the name: the website without protocol, else the clinic. */
function siteOf(l: BoardLead): string {
  const site = l.website
    ?.replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
  return site || l.clinic;
}

export function askReason(stage: Stage): string | null {
  if (!(['lost', 'disq', 'nurture'] as Stage[]).includes(stage)) return '';
  const r = window.prompt(
    stage === 'nurture' ? 'When should we come back, and why?' : 'Reason (saved to the lead):',
    stage === 'nurture' ? 'Write again in 3 months' : 'Not a fit right now',
  );
  return r === null || !r.trim() ? null : r.trim();
}

export function Pipeline() {
  const board = useBoard();
  const team = useTeam();
  const move = useMoveStage();
  const toast = useToast();
  const { open } = useDrawer();
  const mobile = useIsMobile();

  const [market, setMarket] = useLocalState<'all' | 'TR' | 'UK'>('pipe.market', 'all');
  const [src, setSrc] = useLocalState('pipe.src', 'all');
  const [own, setOwn] = useLocalState('pipe.owner', 'all');
  const [warm, setWarm] = useLocalState<'all' | 'warm'>('pipe.warm', 'all');
  const [closed, setClosed] = useLocalState<Stage | null>('pipe.closed', null);
  const [tab, setTab] = useLocalState<Stage | null>('pipe.tab', null);
  const [over, setOver] = useState<string | null>(null);

  const all = board.data ?? [];
  const leads = all.filter(
    (l) =>
      (market === 'all' || l.market === market) &&
      (src === 'all' || l.source === src) &&
      (own === 'all' || l.owner_id === own) &&
      (warm === 'all' || l.warm),
  );
  const sources = [...new Set(all.map((l) => l.source).filter(Boolean))] as string[];
  const active = leads.filter((l) => !isClosed(l.stage)).length;

  function drop(id: string, stage: Stage) {
    setOver(null);
    const lead = all.find((l) => l.id === id);
    if (!lead || lead.stage === stage) return;
    const reason = askReason(stage);
    if (reason === null) return;
    const prev = lead.stage;
    move.mutate(
      { lead: id, stage, reason: reason || null },
      {
        onSuccess: () =>
          toast(`${lead.clinic} moved to ${stageLabel(stage)}`, () => {
            const back = askReason(prev);
            if (back !== null) move.mutate({ lead: id, stage: prev, reason: back || null });
          }),
        onError: (e) => toast(e.message),
      },
    );
  }

  const dnd = (stage: Stage) => ({
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setOver(stage);
    },
    onDragLeave: () => setOver((o) => (o === stage ? null : o)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      drop(e.dataTransfer.getData('text'), stage);
    },
  });

  const card = (l: BoardLead) => (
    <button
      key={l.id}
      className="lcard"
      draggable={!mobile}
      onDragStart={(e) => e.dataTransfer.setData('text', l.id)}
      onClick={() => open({ type: 'lead', id: l.id })}
    >
      <div className="c1">{l.person}</div>
      <div className="c2">{siteOf(l)}</div>
      <div className="c3">
        <SrcBadge source={l.source} />
        {l.warm && <span className="pill sm warm">Warm</span>}
        <span className="mk">{l.market}</span>
        <StepPill lead={l} />
        {l.open_tasks > 0 && <span className="pill sm">{l.open_tasks} open</span>}
        {l.lost_reason && <span className="pill sm gr">{l.lost_reason}</span>}
      </div>
    </button>
  );

  let body;
  if (board.isLoading) {
    body = <div className="card loading">Loading…</div>;
  } else if (mobile) {
    const order = [...OPEN, ...CLOSED];
    const sel =
      (tab && order.find((s) => s.id === tab)?.id) ||
      (OPEN.find((s) => leads.some((l) => l.stage === s.id)) ?? OPEN[0]!).id;
    const inTab = leads.filter((l) => l.stage === sel);
    body = (
      <>
        <div className="stabs">
          {order.map((s) => (
            <button
              key={s.id}
              className="stab"
              aria-pressed={s.id === sel}
              onClick={() => setTab(s.id)}
            >
              {s.label} {leads.filter((l) => l.stage === s.id).length}
            </button>
          ))}
        </div>
        <div>
          {inTab.length ? (
            inTab.map(card)
          ) : (
            <div className="card empty">No leads in this stage.</div>
          )}
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div className="board">
          {OPEN.map((s) => {
            const col = leads.filter((l) => l.stage === s.id);
            return (
              <div key={s.id} className={`colm ${over === s.id ? 'over' : ''}`} {...dnd(s.id)}>
                <h3>
                  {s.label}
                  <span className="cnt">{col.length}</span>
                </h3>
                {col.map(card)}
              </div>
            );
          })}
        </div>
        <div className="closed">
          {CLOSED.map((s) => (
            <button
              key={s.id}
              className={`clc ${over === s.id ? 'over' : ''}`}
              aria-pressed={closed === s.id}
              {...dnd(s.id)}
              onClick={() => setClosed(closed === s.id ? null : s.id)}
            >
              <b>{leads.filter((l) => l.stage === s.id).length}</b>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
        {closed && (CLOSED_STAGES as readonly string[]).includes(closed) && (
          <section className="card sec" style={{ marginTop: 18 }}>
            <Chead label={stageLabel(closed)} cls="g" />
            <div
              style={{
                padding: '4px 22px 16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 12,
              }}
            >
              {leads.filter((l) => l.stage === closed).map(card)}
              {!leads.some((l) => l.stage === closed) && <div className="empty">None.</div>}
            </div>
          </section>
        )}
      </>
    );
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>
            <span className="grad">Pipeline</span>
          </h1>
          <p>
            {active} active leads.{mobile ? '' : ' Drag a card to move it.'}
          </p>
        </div>
        <div className="head-r">
          <div className="seg" role="group" aria-label="Market">
            {(
              [
                ['all', 'All'],
                ['TR', 'Turkey'],
                ['UK', 'UK'],
              ] as const
            ).map(([v, t]) => (
              <button key={v} aria-pressed={market === v} onClick={() => setMarket(v)}>
                {t}
              </button>
            ))}
          </div>
          <button className="btn primary big" onClick={() => open({ type: 'newLead' })}>
            Add lead <Icon name="arrow" />
          </button>
        </div>
      </div>
      <div className="pfilters">
        <select aria-label="Source" value={src} onChange={(e) => setSrc(e.target.value)}>
          <option value="all">All sources</option>
          {sources.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select aria-label="Owner" value={own} onChange={(e) => setOwn(e.target.value)}>
          <option value="all">All owners</option>
          {(team.data ?? [])
            .filter((p) => !p.external)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
        </select>
        <select
          aria-label="Warmth"
          value={warm}
          onChange={(e) => setWarm(e.target.value as 'all' | 'warm')}
        >
          <option value="all">All leads</option>
          <option value="warm">Warm only</option>
        </select>
      </div>
      <ErrorBox error={board.error} />
      {body}
    </>
  );
}
